import React, { useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../services/firebaseService.js';
import { clientLogger } from '../utils/logger.js';

// Reusable SVG icons for buttons
const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.82l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
        <path fill="none" d="M0 0h48v48H0z"></path>
    </svg>
);

const AppleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 16 16">
        <path d="M8.61,4.22C8.83,3.34,9.58,2.13,10.6,2.13c1.4,0,2.15,0.88,2.15,0.88s-0.91,1.52-2.3,1.57 c-1.3,0.05-2.23-0.93-2.23-0.93S8.47,4.8,8.61,4.22z M10.46,8.81c-0.12,0.1-1.88,1.25-1.88,3.23c0,2.5,2.1,3.4,2.1,3.4 s0.19-1.28-0.78-2.28c-0.97-1-1.6-1.55-1.09-2.61c0.51-1.06,2.23-1.4,2.36-1.42C11.33,9.13,10.58,8.71,10.46,8.81z M9.15,1 C6.73,1.05,4.88,2.9,4.88,5.39c0,2.83,2.23,4.13,2.23,4.13s-1.15,3.35-0.03,4.83c1.12,1.48,2.4,1.6,2.4,1.6s0.15-2.35-1.4-3.48 c-1.55-1.13-1.48-3.13-1.48-3.13s2.95-1.05,4.35-0.9c1.4,0.15,3.4,0.75,3.4,3.3c0,3.1-3.2,3.8-3.3,3.8c-0.1,0-1.8-0.5-3.3-2 c-1.5-1.5-2.2-3.8-2.2-3.8s-3.3,0.7-4.4-1.2C1.73,10.29,3.73,5.64,6.28,5.64c1.25,0,2.37-0.75,3.37-0.75 C10.65,4.89,9.4,1,9.15,1z"></path>
    </svg>
);

const FirebaseLogin = ({ onSuccess }) => {
    const [error, setError] = useState(null);

    const handleAuth = async (provider) => {
        setError(null);
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            clientLogger.info('FirebaseLogin: User signed in successfully', { uid: user.uid, email: user.email });
            
            // The parent component will handle the backend sync and redirection
            if (onSuccess) {
                onSuccess(user);
            }
        } catch (authError) {
            clientLogger.error('FirebaseLogin: Authentication error', authError);
            setError(`Error: ${authError.message}`);
        }
    };

    return (
        <div className="space-y-4">
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p className="font-bold">Authentication Failed</p>
                    <p>{error}</p>
                </div>
            )}
            <button
                onClick={() => handleAuth(googleProvider)}
                className="w-full inline-flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
                <GoogleIcon />
                <span className="ml-3">Sign in with Google</span>
            </button>
            <button
                onClick={() => handleAuth(appleProvider)}
                className="w-full inline-flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-black text-sm font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
                <AppleIcon />
                <span className="ml-3">Sign in with Apple</span>
            </button>
        </div>
    );
};

export default FirebaseLogin; 